import { z } from 'zod';
import { COMMON_STRINGS } from '@/lib/constants/common-strings';
import { hasDomainHost, normalizeUrl } from './url.utils';

/**
 * Optional URL field for zod form schemas: blank is allowed; a non-blank value
 * is normalized first (trimmed, `https://` prepended when no scheme was typed)
 * and must then be a valid URL with a domain host (so "acme" is still a field
 * error rather than a Backend 400). The normalized value is the parsed output, so
 * it is what a `zodResolver`-backed submit handler receives and sends.
 */
export const optionalUrlSchema = z.preprocess(
  (value) => (typeof value === 'string' ? normalizeUrl(value) : value),
  z
    .string()
    .url(COMMON_STRINGS.VALIDATION.INVALID_URL)
    .refine(hasDomainHost, COMMON_STRINGS.VALIDATION.INVALID_URL)
    .or(z.literal(''))
    .optional(),
);

/**
 * Required URL field: the same normalization and checks as `optionalUrlSchema`,
 * with blank refused. Used where a website is mandatory (the Issue Report
 * signup and its "Confirm your startup" step, FR-218, FR-225).
 *
 * @param requiredMessage The field error for a blank value.
 */
export function requiredUrlSchema(requiredMessage: string) {
  return z.preprocess(
    (value) => (typeof value === 'string' ? normalizeUrl(value) : value),
    z
      .string()
      .min(1, requiredMessage)
      .url(COMMON_STRINGS.VALIDATION.INVALID_URL)
      .refine(hasDomainHost, COMMON_STRINGS.VALIDATION.INVALID_URL),
  );
}

